"use client";

import type { paths } from "@frend-digital/centra-types/oas";
import { useCentraClient, useToken } from "@frend-digital/centra/client";
import { useQuery } from "@tanstack/react-query";

type Ids = string[];

type UseProductsProps<T = never> = {
  products?: Ids;

  fetcher?: (args: { products?: Ids } & EndpointParams) => Promise<T>;
} & EndpointParams;

type EndpointParams = NonNullable<
  paths["/products"]["post"]["requestBody"]
>["content"]["application/json"];

export const useProducts = <T = undefined>({
  fetcher,
  limit = 10,
  ...rest
}: UseProductsProps<T>) => {
  const token = useToken();
  const client = useCentraClient();

  type Return = T extends undefined
    ? paths["/products"]["post"]["responses"]["200"]["content"]["application/json"]["products"]
    : T;

  const queryData = useQuery({
    queryKey: [
      "products-by-ids",
      limit,
      rest,
      fetcher ? "fetcher" : "no-fetcher",
    ],
    queryFn: async (): Promise<Return> => {
      const body = {
        ...rest,
        limit,
      };

      if (fetcher) {
        return fetcher(body) as Promise<Return>;
      }

      const { data, error } = await client.POST("/products", {
        body,
      });

      if (error) {
        return [] as Return;
      }

      return data.products as Return;
    },
    enabled: !!token,
  });

  return queryData;
};
